import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Check, ArrowRight, Flame, Star, TreePine, Tent, Droplets, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { IMAGES } from "@/lib/constants";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const siteAmenities = [
  "Fire Pit", "Picnic Table", "Restroom Access", "Water Hookup",
  "Parking Space", "Trash Service", "Gated & Secure", "Pet Friendly",
];

const whatToBring = [
  "Tent & sleeping bags", "Flashlight / headlamp", "Insect repellent",
  "Sunscreen", "Camp chairs", "Cooler with ice", "Personal toiletries",
  "Firewood (or purchase on-site)",
];

const experiences = [
  { icon: Flame, title: "Campfire Cooking", desc: "Enjoy cooking over an open flame with dedicated fire pits at every site." },
  { icon: Star, title: "Stargazing", desc: "Clear Texas skies make our campus perfect for stargazing far from city lights." },
  { icon: TreePine, title: "Nature Trails", desc: "Explore walking paths and trails throughout the resort and nearby parks." },
  { icon: Droplets, title: "Lake Access", desc: "Minutes from Lake Texoma for fishing, swimming, and water activities." },
];

const rules = [
  "Check-in: 3:00 PM | Check-out: 11:00 AM",
  "Quiet hours: 10:00 PM - 7:00 AM",
  "Pets must be leashed at all times",
  "No glass containers at campsites",
  "Fires only in designated fire pits",
  "Leave no trace - pack out all trash",
];

export default function CampingPage() {
  return (
    <>
      <Helmet>
        <title>Camping Sites | Campsites Near Lake Texoma - Texoma Resorts</title>
        <meta name="description" content="Book camping sites near Lake Texoma at Texoma Resorts. Gated, secure campsites with fire pits, restrooms, and stargazing. Perfect for families and groups." />
      </Helmet>

      <section className="relative h-[60vh] min-h-[450px] flex items-end">
        <div className="absolute inset-0">
          <img src={IMAGES.camping1} alt="Campfire under the stars near Lake Texoma" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="caption-text text-white/60 mb-3">Outdoor Living</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-white tracking-tight">
              Camping Sites
            </h1>
            <p className="font-body text-base md:text-lg text-white/70 max-w-xl mt-4">
              Classic outdoor camping in a gated, secure resort near Lake Texoma
            </p>
          </motion.div>
        </div>
      </section>

      <section data-testid="camping-overview" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div {...fadeUp}>
              <p className="caption-text text-accent mb-3">Camping at Serenity Village</p>
              <h2 className="font-heading text-3xl md:text-4xl font-normal text-foreground mb-6">
                Connect With Nature
              </h2>
              <p className="font-body text-base text-muted-foreground leading-relaxed mb-4">
                Our gated, secure camping sites offer the traditional outdoor experience
                with modern conveniences. Set up camp among the trees, gather around the
                fire pit, and enjoy the sounds of nature just minutes from Lake Texoma.
              </p>
              <p className="font-body text-base text-muted-foreground leading-relaxed mb-8">
                Perfect for families, groups of friends, or anyone looking to unplug and
                reconnect with the great outdoors. Each site includes a dedicated fire pit,
                picnic table, and parking space.
              </p>

              <h3 className="font-heading text-xl font-semibold text-foreground mb-4">Site Amenities</h3>
              <div className="grid grid-cols-2 gap-3 mb-8">
                {siteAmenities.map((a) => (
                  <div key={a} className="flex items-center gap-2">
                    <Check size={14} className="text-primary shrink-0" />
                    <span className="font-body text-sm text-foreground/80">{a}</span>
                  </div>
                ))}
              </div>

              <Link to="/contact">
                <Button data-testid="camping-reserve" className="bg-accent hover:bg-accent/90 text-white font-body rounded-full px-8">
                  Reserve Your Campsite <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </motion.div>

            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 img-hover-zoom rounded-lg overflow-hidden aspect-video">
                  <img src={IMAGES.camping2} alt="Campfire at night" className="w-full h-full object-cover" />
                </div>
                <div className="img-hover-zoom rounded-lg overflow-hidden aspect-square">
                  <img src={IMAGES.camping3} alt="Campsite bonfire" className="w-full h-full object-cover" />
                </div>
                <div className="img-hover-zoom rounded-lg overflow-hidden aspect-square">
                  <img src={IMAGES.gallery6} alt="Camping at the resort" className="w-full h-full object-cover" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-12">
            <p className="caption-text text-accent mb-3">Outdoor Adventures</p>
            <h2 className="font-heading text-3xl md:text-4xl font-normal text-foreground">
              Campfire & Outdoor Experiences
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {experiences.map((e, i) => (
              <motion.div key={e.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
                <Card className="border border-border/50 h-full">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/5 flex items-center justify-center">
                      <e.icon size={22} className="text-primary" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">{e.title}</h3>
                    <p className="font-body text-sm text-muted-foreground">{e.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div {...fadeUp}>
              <div className="flex items-center gap-2 mb-4">
                <Tent size={20} className="text-primary" />
                <h3 className="font-heading text-2xl font-normal text-foreground">What to Bring</h3>
              </div>
              <div className="space-y-3">
                {whatToBring.map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <Check size={14} className="text-accent shrink-0" />
                    <span className="font-body text-sm text-foreground/80">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.15 }}>
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck size={20} className="text-primary" />
                <h3 className="font-heading text-2xl font-normal text-foreground">Camping Rules</h3>
              </div>
              <div className="space-y-3">
                {rules.map((rule) => (
                  <div key={rule} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />
                    <span className="font-body text-sm text-foreground/80">{rule}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-foreground tracking-tight mb-4">
              Plan Your Outdoor Adventure
            </h2>
            <p className="font-body text-base text-muted-foreground mb-8">
              Book your camping site at Texoma Resorts and experience the beauty
              of Lake Texoma up close.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/contact">
                <Button data-testid="camping-book-cta" className="bg-primary hover:bg-primary/90 text-white font-body rounded-full px-8">
                  Book Camping Near Lake Texoma
                </Button>
              </Link>
              <Link to="/experiences">
                <Button variant="outline" className="font-body rounded-full px-8 border-primary text-primary hover:bg-primary hover:text-white">
                  Add Experiences
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
